import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../../auth-lib';
import { Select, Button } from '../../../design-system';
import MultiSelect from './MultiSelect';
import {
  useReport, ReportCard, StatRow, Stat, BarList, StackedBars,
  fmtNum, fmtHours, fmtMoney, fmtMoneyShort, fmtPct, pctTone, toneColor,
} from './reportsKit';

// Work Done register — one searchable line list of everything the firm did:
// statutory filings for every service head PLUS the extra (ad-hoc / catalog
// service / client-request / recurring) tasks that never touch the compliance
// calendar. All filtering happens server-side so the KPI tiles, the charts and
// the CSV export always describe the same filtered set as the table.

const BASIS = [
  { value: 'done', label: 'Completed date' },
  { value: 'due', label: 'Due date' },
  { value: 'created', label: 'Created date' },
];

const STATUS_OPTS = [
  { value: 'done', label: 'Done / filed' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'na', label: 'Not applicable' },
];

const CATEGORY_OPTS = [
  { value: 'compliance', label: 'Statutory compliance' },
  { value: 'service', label: 'Service task' },
  { value: 'adhoc', label: 'Extra / ad-hoc task' },
];

const PRIORITY_OPTS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const SORTS = [
  { value: 'date', label: 'Date — newest' },
  { value: 'date-asc', label: 'Date — oldest' },
  { value: 'client', label: 'Client A–Z' },
  { value: 'service', label: 'Service A–Z' },
  { value: 'dueDate', label: 'Due date' },
  { value: 'status', label: 'Status (worst first)' },
  { value: 'hours', label: 'Hours logged' },
  { value: 'amount', label: 'Amount charged' },
];

// Where the money on a row has got to. The register knows this because the
// server resolves each task charge to the invoice it landed on and that
// invoice's settlement — the same ledger the Finance module writes.
const BILLING_OPTS = [
  { value: 'unbilled', label: 'Priced, not invoiced' },
  { value: 'drafted', label: 'On a draft / proforma' },
  { value: 'invoiced', label: 'Invoiced, awaiting payment' },
  { value: 'overdue', label: 'Invoice overdue' },
  { value: 'paid', label: 'Paid' },
  { value: 'none', label: 'No charge priced' },
];

const BILLING_META = {
  unbilled: { label: 'Not invoiced', cls: 'wd-bl-unbilled' },
  drafted: { label: 'Draft', cls: 'wd-bl-drafted' },
  invoiced: { label: 'Invoiced', cls: 'wd-bl-invoiced' },
  overdue: { label: 'Overdue', cls: 'wd-bl-overdue' },
  paid: { label: 'Paid', cls: 'wd-bl-paid' },
  none: { label: '—', cls: 'wd-bl-none' },
};

const STATUS_META = {
  done: { label: 'Done', cls: 'wd-st-done' },
  pending: { label: 'Pending', cls: 'wd-st-pending' },
  overdue: { label: 'Overdue', cls: 'wd-st-overdue' },
  na: { label: 'N/A', cls: 'wd-st-na' },
};

const CATEGORY_META = {
  compliance: { label: 'Compliance', cls: 'wd-cat-compliance' },
  service: { label: 'Service', cls: 'wd-cat-service' },
  adhoc: { label: 'Extra', cls: 'wd-cat-adhoc' },
};

const PAGE_SIZES = [50, 100, 250, 500];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');
const taskLabel = (n) => (n == null ? '' : `TASK-${String(n).padStart(4, '0')}`);
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

function WorkDoneReports({ range }) {
  const [options, setOptions] = useState({ clients: [], types: [], services: [], users: [] });
  const [basis, setBasis] = useState('done');
  const [statuses, setStatuses] = useState(['done']);
  const [categories, setCategories] = useState([]);
  const [clientIds, setClientIds] = useState([]);
  const [types, setTypes] = useState([]);
  const [serviceIds, setServiceIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [billable, setBillable] = useState('all');
  const [billingStatuses, setBillingStatuses] = useState([]);
  const [sort, setSort] = useState('date');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    apiClient
      .get('/api/reports/work-done/options')
      .then((r) => setOptions(r.data || { clients: [], types: [], services: [], users: [] }))
      .catch(() => {});
  }, []);

  // Typing shouldn't fire a request per keystroke — the register is a full
  // collection walk on the server.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any change to what's being asked for invalidates the page number — page 7
  // of the old result set is meaningless against the new one.
  const filterKey = JSON.stringify([range, basis, statuses, categories, clientIds, types, serviceIds, userIds, priorities, billable, billingStatuses, search, pageSize]);
  useEffect(() => { setPage(1); }, [filterKey]);

  const params = useMemo(() => {
    const p = { ...range, basis, sort, page, limit: pageSize };
    // An empty selection means "no filter"; sending an empty string would be
    // read as one by the server's csvSet, so omit the key entirely.
    if (statuses.length) p.statuses = statuses.join(',');
    if (categories.length) p.categories = categories.join(',');
    if (clientIds.length) p.clientIds = clientIds.join(',');
    if (types.length) p.types = types.join(',');
    if (serviceIds.length) p.serviceIds = serviceIds.join(',');
    if (userIds.length) p.userIds = userIds.join(',');
    if (priorities.length) p.priorities = priorities.join(',');
    if (billable !== 'all') p.billable = billable;
    if (billingStatuses.length) p.billingStatuses = billingStatuses.join(',');
    if (search) p.search = search;
    return p;
  }, [range, basis, sort, page, pageSize, statuses, categories, clientIds, types, serviceIds, userIds, priorities, billable, billingStatuses, search]);

  const { data, loading, error } = useReport('work-done', params);

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const resetFilters = () => {
    setBasis('done');
    setStatuses(['done']);
    setCategories([]);
    setClientIds([]);
    setTypes([]);
    setServiceIds([]);
    setUserIds([]);
    setPriorities([]);
    setBillable('all');
    setBillingStatuses([]);
    setSearchInput('');
    setSort('date');
  };

  const activeFilters =
    (statuses.length === 1 && statuses[0] === 'done' ? 0 : 1) +
    [categories, clientIds, types, serviceIds, userIds, priorities, billingStatuses].filter((a) => a.length).length +
    (billable !== 'all' ? 1 : 0) +
    (search ? 1 : 0);

  // Export the whole filtered set, not just the visible page — limit=0 asks the
  // server for every row it matched.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get('/api/reports/work-done', { params: { ...params, page: 1, limit: 0 } });
      const all = res.data?.rows || [];
      // The billing columns are the point of the export for anyone reconciling:
      // one line per piece of work, with what it was charged at, whether that
      // reached an invoice, and how much of it has come back.
      const header = ['Date', 'Type', 'Client', 'Client code', 'Service / head', 'Work', 'Task no', 'Period',
        'GSTIN', 'Due date', 'Completed', 'Status', 'On time', 'Preparer', 'Reviewer', 'Hours',
        'Allocated hours', 'Charged', 'Invoiced', 'Not invoiced', 'Collected', 'Outstanding',
        'Billing status', 'Invoice no', 'Ack no'];
      const body = all.map((r) => [
        fmtDate(r.refDate), CATEGORY_META[r.category]?.label || r.category, r.client, r.clientCode,
        r.service, r.title, taskLabel(r.taskNumber), r.period, r.gstin,
        fmtDate(r.dueDate), fmtDate(r.doneDate), STATUS_META[r.status]?.label || r.status,
        r.onTime === null ? '' : r.onTime ? 'Yes' : 'No',
        r.preparer || '', r.reviewer || '', r.hours, r.allocatedHours,
        r.accruedAmount || 0, r.invoicedAmount || 0, r.unbilledAmount || 0,
        r.collectedAmount || 0, r.outstandingAmount || 0,
        BILLING_META[r.billingStatus]?.label || r.billingStatus,
        (r.invoiceNumbers || []).join(' / '), r.acknowledgmentNo,
      ]);
      const csv = [header, ...body].map((line) => line.map(csvCell).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `work-done-${range.from}-to-${range.to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* the card already surfaces load errors; a failed export just does nothing */
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="rep-stack">
      <ReportCard
        title="Work Done Register"
        subtitle="Every statutory filing and every extra task in one line list — filter, search and export"
        icon="🧾"
        loading={loading}
        error={error}
        action={
          <div className="wd-actions">
            <Select value={basis} onChange={(e) => setBasis(e.target.value)} aria-label="Date basis">
              {BASIS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </Select>
            <Select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort by">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </div>
        }
      >
        <StatRow>
          <Stat label="Work items" value={fmtNum(summary.rows)} tone="accent" hint={`${fmtNum(summary.byCategory?.compliance)} compliance · ${fmtNum((summary.byCategory?.service || 0) + (summary.byCategory?.adhoc || 0))} extra`} />
          <Stat label="Done" value={fmtNum(summary.done)} tone="green" hint={summary.completionPct == null ? undefined : `${summary.completionPct}% of shown`} />
          <Stat label="Pending" value={fmtNum(summary.pending)} tone="amber" />
          <Stat label="Overdue" value={fmtNum(summary.overdue)} tone="red" />
          <Stat label="On time" value={fmtPct(summary.onTimePct)} tone={pctTone(summary.onTimePct)} hint={`${fmtNum(summary.onTime)} of ${fmtNum(summary.onTimeEligible)} datable`} />
          <Stat label="Hours logged" value={fmtHours(summary.hours)} tone="blue" hint={`${fmtHours(summary.allocatedHours)} budgeted`} />
        </StatRow>

        {/* The money on the work shown, at each stage. This row is what
            connects the register to Finance: "Charged" is what these tasks were
            priced at, and everything to its right comes from the invoice ledger
            — so a filing that was done, priced and then never billed is visible
            on the same screen that says the filing was done. */}
        <StatRow>
          <Stat
            label="Charged"
            value={fmtMoneyShort(summary.accrued)}
            tone="slate"
            hint={`${fmtNum(summary.billableRows)} of ${fmtNum(summary.rows)} items carry a charge`}
          />
          <Stat
            label="Not invoiced"
            value={fmtMoneyShort(summary.unbilled)}
            tone={summary.unbilled > 0 ? 'amber' : 'green'}
            hint={`${fmtNum(summary.byBillingStatus?.unbilled)} items never billed`}
          />
          <Stat
            label="Invoiced"
            value={fmtMoneyShort(summary.invoiced)}
            tone="accent"
            hint={summary.billedPct == null ? 'Nothing charged yet' : `${summary.billedPct}% of what was charged`}
          />
          <Stat
            label="Collected"
            value={fmtMoneyShort(summary.collected)}
            tone="green"
            hint={summary.realisationPct == null ? 'Nothing invoiced yet' : `${summary.realisationPct}% of invoiced`}
          />
          <Stat
            label="Still owed"
            value={fmtMoneyShort(summary.outstanding)}
            tone={summary.byBillingStatus?.overdue > 0 ? 'red' : summary.outstanding > 0 ? 'amber' : 'green'}
            hint={`${fmtNum(summary.byBillingStatus?.overdue)} on an overdue invoice`}
          />
          {summary.drafted > 0 && (
            <Stat
              label="On a draft"
              value={fmtMoneyShort(summary.drafted)}
              tone="blue"
              hint={`${fmtNum(summary.byBillingStatus?.drafted)} items on an unissued document`}
            />
          )}
        </StatRow>

        {/* ── Filters ── */}
        <div className="wd-filters">
          <input
            className="ftrk-search wd-search"
            placeholder="🔎 Search client, code, task no, service, period, GSTIN, ack no, staff…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <MultiSelect label="Status" options={STATUS_OPTS} selected={statuses} onChange={setStatuses} allLabel="Any status" />
          <MultiSelect label="Kind" options={CATEGORY_OPTS} selected={categories} onChange={setCategories} allLabel="All kinds" />
          <MultiSelect label="Client" options={options.clients} selected={clientIds} onChange={setClientIds} allLabel="All clients" />
          <MultiSelect label="Head" options={options.types} selected={types} onChange={setTypes} allLabel="All heads" />
          <MultiSelect label="Service" options={options.services} selected={serviceIds} onChange={setServiceIds} allLabel="All services" />
          <MultiSelect label="Staff" options={options.users} selected={userIds} onChange={setUserIds} allLabel="Anyone" />
          <MultiSelect label="Priority" options={PRIORITY_OPTS} selected={priorities} onChange={setPriorities} allLabel="Any priority" />
          <MultiSelect label="Billing" options={BILLING_OPTS} selected={billingStatuses} onChange={setBillingStatuses} allLabel="Any billing state" />
          {/* A span, not a label: Select renders its own wrapper div, and a
              <label> around one would associate with nothing useful. The
              aria-label on the Select itself does the naming. */}
          <span className="rep-ms">
            <span className="rep-ms-label">Charged</span>
            <Select value={billable} onChange={(e) => setBillable(e.target.value)} aria-label="Charged">
              <option value="all">Any</option>
              <option value="yes">Has a charge</option>
              <option value="no">No charge</option>
            </Select>
          </span>
          <span style={{ flex: 1 }} />
          {activeFilters > 0 && (
            <Button variant="secondary" size="sm" onClick={resetFilters}>Reset ({activeFilters})</Button>
          )}
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={exporting || total === 0}>
            {exporting ? 'Exporting…' : `Export ${fmtNum(total)}`}
          </Button>
        </div>

        {/* ── Register ── */}
        {rows.length === 0 ? (
          <p className="rep-empty">No work matches these filters in this period.</p>
        ) : (
          <>
            <div className="ftrk-scroll">
              <table className="ftrk-table wd-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Service / head</th>
                    <th className="wd-th-work">Work</th>
                    <th>Period</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th>Staff</th>
                    <th className="wd-num">Hrs</th>
                    <th className="wd-num">Charged</th>
                    <th>Billing</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const st = STATUS_META[r.status] || STATUS_META.na;
                    const cat = CATEGORY_META[r.category];
                    const bill = BILLING_META[r.billingStatus] || BILLING_META.none;
                    return (
                      <tr key={r.id}>
                        <td className="wd-nowrap">{fmtDate(r.refDate)}</td>
                        <td>
                          <span className="ftrk-name">{r.client}</span>
                          {r.clientCode && <span className="ftrk-code">{r.clientCode}</span>}
                          {(r.otherClients || []).length > 0 && (
                            <span className="ftrk-code">+{r.otherClients.length} more</span>
                          )}
                        </td>
                        <td>
                          <span className={`wd-cat ${cat?.cls || ''}`}>{cat?.label}</span>
                          <span className="wd-service">{r.service}</span>
                        </td>
                        <td className="wd-th-work">
                          {r.taskId ? (
                            <Link className="wd-link" to={`/org-admin/tasks/${r.taskId}`}>{r.title}</Link>
                          ) : (
                            <span>{r.title}</span>
                          )}
                          {r.taskNumber != null && <span className="ftrk-code">{taskLabel(r.taskNumber)}</span>}
                          {r.acknowledgmentNo && <span className="ftrk-code">Ack {r.acknowledgmentNo}</span>}
                        </td>
                        <td className="wd-nowrap">{r.period || '—'}</td>
                        <td className="wd-nowrap">{fmtDate(r.dueDate)}</td>
                        <td>
                          <span className={`wd-st ${st.cls}`}>{st.label}</span>
                          {r.onTime === false && <span className="wd-late">late</span>}
                        </td>
                        <td className="wd-staff">{r.preparer || r.people?.[0] || '—'}</td>
                        <td className="wd-num">{r.hours ? fmtHours(r.hours) : '—'}</td>
                        <td className="wd-num">{r.accruedAmount ? fmtMoney(r.accruedAmount) : '—'}</td>
                        <td>
                          {r.billingStatus === 'none' ? (
                            <span className="wd-bl-none">—</span>
                          ) : (
                            <>
                              <span className={`wd-bl ${bill.cls}`}>{bill.label}</span>
                              {(r.invoiceNumbers || []).length > 0 && (
                                <span className="ftrk-code">{r.invoiceNumbers.join(' / ')}</span>
                              )}
                              {/* Partly invoiced: the rest of the charge is
                                  still the firm's to bill, and the badge alone
                                  would not say so. */}
                              {r.unbilledAmount > 0 && r.invoicedAmount > 0 && (
                                <span className="ftrk-code">{fmtMoney(r.unbilledAmount)} still to bill</span>
                              )}
                              {r.outstandingAmount > 0 && (
                                <span className="ftrk-code">{fmtMoney(r.outstandingAmount)} owed</span>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="wd-pager">
              <span className="ftrk-selcount">
                Showing {fmtNum((page - 1) * pageSize + 1)}–{fmtNum(Math.min(page * pageSize, total))} of {fmtNum(total)}
              </span>
              <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
              </Select>
              <span style={{ flex: 1 }} />
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="ftrk-selcount">Page {page} of {totalPages}</span>
              <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </>
        )}
      </ReportCard>

      <ReportCard title="Volume over time" subtitle="Work items by month, on the chosen date basis" icon="📈">
        <StackedBars
          data={data?.byMonth || []}
          series={[
            { key: 'done', label: 'Done', color: toneColor('green') },
            { key: 'other', label: 'Open', color: toneColor('amber') },
          ]}
        />
      </ReportCard>

      <div className="rep-grid">
        <ReportCard title="By service / head" subtitle="Top 15 by volume" icon="🗂️">
          <BarList items={(data?.byService || []).map((s) => ({ label: s.label, value: s.rows }))} />
        </ReportCard>
        <ReportCard title="By client" subtitle="Top 15 by volume" icon="🏢">
          <BarList items={(data?.byClient || []).map((s) => ({ label: s.label, value: s.rows }))} />
        </ReportCard>
        <ReportCard title="By preparer" subtitle="Who did the work" icon="👤">
          <BarList items={(data?.byStaff || []).map((s) => ({ label: s.label, value: s.rows }))} color={toneColor('blue')} />
        </ReportCard>
        <ReportCard title="Hours by service / head" subtitle="Logged effort" icon="⏱️">
          <BarList
            items={(data?.byService || []).filter((s) => s.hours > 0).map((s) => ({ label: s.label, value: s.hours }))}
            valueFmt={fmtHours}
            color={toneColor('accent')}
            emptyText="No time logged against these items"
          />
        </ReportCard>
        <ReportCard title="Charged vs invoiced" subtitle="By service / head — the gap is work not yet billed" icon="💰">
          <BarList
            items={(data?.byService || []).filter((s) => s.accrued > 0).map((s) => ({
              label: s.unbilled > 0 ? `${s.label} · ${fmtMoneyShort(s.unbilled)} unbilled` : s.label,
              value: s.accrued,
              color: toneColor(s.unbilled > 0 ? 'amber' : 'green'),
            }))}
            valueFmt={fmtMoneyShort}
            emptyText="Nothing charged against these items"
          />
        </ReportCard>
        <ReportCard title="Money still to bill" subtitle="Clients with work priced and never invoiced" icon="🚰">
          <BarList
            items={(data?.byClient || []).filter((c) => c.unbilled > 0)
              .sort((a, b) => b.unbilled - a.unbilled)
              .map((c) => ({ label: c.label, value: c.unbilled }))}
            valueFmt={fmtMoneyShort}
            color={toneColor('red')}
            emptyText="Every charge on this work has reached an invoice."
          />
        </ReportCard>
      </div>
    </div>
  );
}

export default WorkDoneReports;
